import { useState, useRef, useEffect, useCallback } from "react";
import { useRpc, useCommand } from "../store";
import { useI18n } from "../i18n";
import { useColors, useStyles } from "../ui";
import { uuidv7 } from "../../../shared/src/ids";

interface UnitItem {
  id: string;
  name: string | null;
}

export function UnitAutocomplete({
  value,
  onSelect,
  inputStyle,
  placeholder,
}: {
  /** Display name of the currently selected unit */
  value: string;
  /** Called when a unit is selected (existing or newly created) */
  onSelect: (unit: { id: string; name: string }) => void;
  inputStyle?: React.CSSProperties;
  placeholder?: string;
}) {
  const rpc = useRpc();
  const dispatch = useCommand();
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();
  const [suggestions, setSuggestions] = useState<UnitItem[]>([]);
  const [open, setOpen] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setLocalValue(value);
  }, [value]);

  const inputRef = useRef<HTMLInputElement>(null);

  const search = useCallback(
    async (term: string) => {
      try {
        const result = await rpc<{ units: UnitItem[] }>({
          type: "searchUnits",
          search: term || undefined,
        });
        setSuggestions(result.units);
        setOpen(true);
      } catch {
        setSuggestions([]);
      }
    },
    [rpc],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalValue(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelectExisting = (unit: UnitItem) => {
    setLocalValue(unit.name || "");
    setOpen(false);
    setSuggestions([]);
    onSelect({ id: unit.id, name: unit.name || "" });
  };

  const handleCreate = async () => {
    const name = localValue.trim();
    if (!name) return;
    const uuid = uuidv7();
    await dispatch("unit.create", { uuid });
    await dispatch("unit.setName", { uuid, name });
    setOpen(false);
    setSuggestions([]);
    onSelect({ id: uuid, name });
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const trimmed = localValue.trim();
  const exactMatch = suggestions.some(
    (u) => u.name?.toLowerCase() === trimmed.toLowerCase(),
  );
  const showCreate = trimmed.length > 0 && !exactMatch;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        style={inputStyle || styles.inputCompact}
        value={localValue}
        onChange={handleChange}
        onFocus={() => {
          focusedRef.current = true;
          search(trimmed);
        }}
        onBlur={() => {
          focusedRef.current = false;
        }}
        placeholder={placeholder || t("patient.unit")}
      />
      {open && (suggestions.length > 0 || showCreate) && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxHeight: "240px",
            overflowY: "auto",
          }}
        >
          {suggestions.map((u) => (
            <div
              key={u.id}
              onClick={() => handleSelectExisting(u)}
              style={{
                padding: "6px 8px",
                cursor: "pointer",
                fontSize: "0.825rem",
                borderBottom: `1px solid ${colors.border}`,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = colors.bgAlt)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <span style={{ color: colors.text }}>{u.name || "\u2014"}</span>
            </div>
          ))}
          {showCreate && (
            <div
              onClick={handleCreate}
              style={{
                padding: "6px 8px",
                cursor: "pointer",
                fontSize: "0.825rem",
                color: colors.primary,
                fontWeight: 500,
                borderTop:
                  suggestions.length > 0
                    ? `1px solid ${colors.border}`
                    : undefined,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = colors.bgAlt)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              + {trimmed}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
